import { MouseEvent as ReactMouseEvent, ReactNode, useRef, useState } from "react";
import { Box, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip, alpha } from "@mui/material";
import Iconify from "../../../minimals/components/iconify";
import { DocumentsOrderInfo } from "../types";

// Icons ported from the Momentum order page rail so the two apps stay visually identical.

const stateIntelIds = new Map<string, number>([
  ["AL", 1],
  ["AK", 2],
  ["AZ", 3],
  ["AR", 4],
  ["CA", 5],
  ["CO", 6],
  ["CT", 7],
  ["DC", 8],
  ["DE", 9],
  ["FL", 10],
  ["GA", 11],
  ["HI", 12],
  ["ID", 13],
  ["IL", 14],
  ["IN", 15],
  ["IA", 16],
  ["KS", 17],
  ["KY", 18],
  ["LA", 19],
  ["ME", 20],
  ["MD", 21],
  ["MA", 22],
  ["MI", 23],
  ["MN", 24],
  ["MS", 25],
  ["MO", 26],
  ["MT", 27],
  ["NE", 28],
  ["NV", 29],
  ["NH", 30],
  ["NJ", 31],
  ["NM", 32],
  ["NY", 33],
  ["NC", 34],
  ["ND", 35],
  ["OH", 36],
  ["OK", 37],
  ["OR", 38],
  ["PA", 39],
  ["RI", 40],
  ["SC", 41],
  ["SD", 42],
  ["TN", 43],
  ["TX", 44],
  ["UT", 45],
  ["VT", 46],
  ["VA", 47],
  ["WA", 48],
  ["WV", 49],
  ["WI", 50],
  ["WY", 51]
]);

// Madex (1031 exchange) orders send Intel straight to the exchange site.
const EXCHANGE_INTEL_URL = "https://1031.madisondocs.com/";

// Regular orders go straight to Intel's state search for the property's state
// (matches the Momentum order page).
const buildStateIntelUrl = (stateCode?: string) => {
  const id = stateCode ? stateIntelIds.get(stateCode.toUpperCase()) : undefined;
  return id
    ? `https://madisonintel.com/search/states/${id}/filter/Contacts`
    : "https://madisonintel.com/search/states/no_state/filter/Contacts";
};

function CommitmentIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12.875 9.375C13.1071 9.60693 13.4219 9.7372 13.75 9.7372C14.0781 9.7372 14.3929 9.60693 14.625 9.375C14.8569 9.14286 14.9872 8.82814 14.9872 8.5C14.9872 8.17186 14.8569 7.85714 14.625 7.625L10 3"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M0.5 5.5V0.5H5.55C5.70046 0.500099 5.85077 0.509785 6 0.529"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.5 8L13.013 9.513C13.2449 9.74514 13.3752 10.0599 13.3752 10.388C13.3752 10.7161 13.2449 11.0309 13.013 11.263C12.7809 11.4949 12.4662 11.6252 12.138 11.6252C11.8099 11.6252 11.4952 11.4949 11.263 11.263L9.73602 9.736"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.875 10.875C11.1069 11.1071 11.2372 11.4219 11.2372 11.75C11.2372 12.0781 11.1069 12.3929 10.875 12.625"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 3L6.55103 5.587C6.38541 5.71107 6.18854 5.78661 5.98243 5.80515C5.77633 5.82369 5.56913 5.78452 5.38403 5.692C5.22594 5.61302 5.08889 5.4976 4.98417 5.35524C4.87945 5.21289 4.81007 5.04769 4.78175 4.87325C4.75342 4.69881 4.76697 4.52014 4.82126 4.35197C4.87556 4.1838 4.96905 4.03094 5.09403 3.906L8.15303 1.239C8.69921 0.762691 9.39934 0.500186 10.124 0.5H15.5V5.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.87495 7.12534L1.62463 7.37566C1.14155 7.85874 1.14155 8.64196 1.62463 9.12504L1.62534 9.12575C2.10842 9.60883 2.89164 9.60883 3.37472 9.12575L3.62504 8.87543C4.10811 8.39235 4.10811 7.60913 3.62504 7.12605L3.62433 7.12534C3.14125 6.64227 2.35802 6.64227 1.87495 7.12534Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.69514 8.80605L3.30694 9.19425C2.82386 9.67733 2.82386 10.4606 3.30694 10.9436L3.30765 10.9443C3.79073 11.4274 4.57395 11.4274 5.05703 10.9443L5.44523 10.5561C5.92831 10.0731 5.92831 9.28983 5.44523 8.80675L5.44453 8.80605C4.96145 8.32297 4.17822 8.32297 3.69514 8.80605Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.30701 10.6928L5.0567 10.9431C4.57362 11.4262 4.57362 12.2094 5.0567 12.6925L5.0574 12.6932C5.54048 13.1763 6.32371 13.1763 6.80679 12.6932L7.0571 12.4429C7.54018 11.9598 7.54018 11.1766 7.0571 10.6935L7.0564 10.6928C6.57332 10.2097 5.79009 10.2097 5.30701 10.6928Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.07695 12.4235L6.81744 12.683C6.40309 13.0974 6.40294 13.769 6.81709 14.1831C7.23124 14.5973 7.90287 14.5971 8.31721 14.1828L8.57672 13.9233C8.99107 13.5089 8.99123 12.8373 8.57708 12.4232C8.16292 12.009 7.49129 12.0092 7.07695 12.4235Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MoneyTransferIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 6.5V17.5M14.5 9H10.75C9.784 9 9 9.784 9 10.75C9 11.716 9.784 12.5 10.75 12.5H13.25C14.216 12.5 15 13.284 15 14.25C15 15.216 14.216 16 13.25 16H9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IntelIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 18 20" xmlns="http://www.w3.org/2000/svg">
      <g fill="currentColor" fillRule="evenodd">
        <path d="M.25 1.609h17.272V0H.25zM4.061 19.73H5.67V8.042H4.06zM8.082 19.73h1.609V8.042H8.082zM12.103 19.73h1.609V8.042h-1.61zM15.486 5.63h-13.2a.803.803 0 1 1 0-1.61h13.2a.804.804 0 0 1 0 1.61" />
      </g>
    </svg>
  );
}

function ReadIcon() {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function ChecklistIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M3.75 16.25h3.5v3.5h-3.5zm0-6h3.5v3.5h-3.5zm0-4.036L5.063 7.5l2.187-3M10 6h10.25M10 12h10.25M10 18h10.25"
      />
    </svg>
  );
}

function RecordablesIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="currentColor"
        d="M2.5 1.75v11.5c0 .138.112.25.25.25h3.17a.75.75 0 0 1 0 1.5H2.75A1.75 1.75 0 0 1 1 13.25V1.75C1 .784 1.784 0 2.75 0h8.5C12.216 0 13 .784 13 1.75v7.736a.75.75 0 0 1-1.5 0V1.75a.25.25 0 0 0-.25-.25h-8.5a.25.25 0 0 0-.25.25Zm13.274 9.537v-.001l-4.557 4.45a.75.75 0 0 1-1.055-.008l-1.943-1.95a.75.75 0 0 1 1.062-1.058l1.419 1.425l4.026-3.932a.75.75 0 1 1 1.048 1.074ZM4.75 4h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1 0-1.5ZM4 7.75A.75.75 0 0 1 4.75 7h2a.75.75 0 0 1 0 1.5h-2A.75.75 0 0 1 4 7.75Z"
      />
    </svg>
  );
}

// Ported from the Momentum rail so the Madex exchange reports use the same glyphs.
function DisbursementSummaryIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 192.287 192.287" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M122.901,0H19.699v192.287h152.889v-142.6L122.901,0z M146.981,45.299h-19.686V25.612L146.981,45.299z M34.699,177.287V15h77.596v37.799c0,4.142,3.357,7.5,7.5,7.5h37.793v116.988H34.699z" />
      <rect x="53.141" y="149.004" width="86.006" height="10" />
      <rect x="53.141" y="55.101" width="51.058" height="10" />
      <polygon points="121.248,86.935 126.79,86.935 105.371,108.353 88.623,91.605 51.597,128.634 58.667,135.706 88.623,105.748 105.371,122.495 133.861,94.005 133.861,99.535 143.861,99.535 143.861,76.935 121.248,76.935" />
      <rect x="53.141" y="33.283" width="51.058" height="10" />
    </svg>
  );
}

function DisbursementAuthorizationIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 184.153 184.153" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M129.318,0H26.06c-1.919,0-3.475,1.554-3.475,3.475v177.203c0,1.92,1.556,3.475,3.475,3.475h132.034c1.919,0,3.475-1.554,3.475-3.475V34.131C161.568,22.011,140.771,0,129.318,0z M154.62,177.203H29.535V6.949h99.784c7.803,0,25.301,18.798,25.301,27.182V177.203z" />
      <path d="M71.23,76.441c15.327,0,27.797-12.47,27.797-27.797c0-15.327-12.47-27.797-27.797-27.797c-15.327,0-27.797,12.47-27.797,27.797C43.433,63.971,55.902,76.441,71.23,76.441z M71.229,27.797c11.497,0,20.848,9.351,20.848,20.847c0,0.888-0.074,1.758-0.183,2.617l-18.071-2.708L62.505,29.735C65.162,28.503,68.112,27.797,71.229,27.797z M56.761,33.668l11.951,19.869c0.534,0.889,1.437,1.49,2.462,1.646l18.669,2.799c-3.433,6.814-10.477,11.51-18.613,11.51c-11.496,0-20.847-9.351-20.847-20.847C50.381,42.767,52.836,37.461,56.761,33.668z" />
      <rect x="46.907" y="90.339" width="73.058" height="6.949" />
      <rect x="46.907" y="107.712" width="48.644" height="6.949" />
      <rect x="46.907" y="125.085" width="62.542" height="6.949" />
    </svg>
  );
}

export const NAV_RAIL_WIDTH = 56;

// The app header is a plain MUI Toolbar (64px at >=600px), so the rail starts flush
// beneath it rather than tracking the taller sticky offset the page toolbars use.
const HEADER_HEIGHT = 64;

type RailMenuItem = {
  label: string;
  iconifyIcon: string;
  href: string;
  external?: boolean;
};

type RailLink = {
  label: string;
  icon: ReactNode;
  href?: string;
  external?: boolean;
  current?: boolean;
  menuItems?: RailMenuItem[];
};

const railButtonSx = (current?: boolean) => ({
  position: "relative" as const,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: "5px",
  cursor: "pointer",
  color: "#fff",
  backgroundColor: current ? "#00b8d4" : "#1f2b5e",
  "& svg": { maxHeight: 20, width: "auto" },
  "&:hover": {
    color: current ? "#fff" : "#00b8d4"
  },
  ...(current && {
    "&::before": {
      content: '""',
      position: "absolute",
      left: -10,
      width: "3px",
      height: 40,
      borderRadius: "5px",
      backgroundColor: "#00b8d4"
    }
  })
});

function RailMenuButton({ link }: { link: RailLink }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Hover-triggered: a short close delay bridges the gap between the rail button
  // and the menu so moving the cursor into the menu doesn't dismiss it.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const items = link.menuItems ?? [];

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openMenu = (event: ReactMouseEvent<HTMLElement>) => {
    cancelClose();
    setAnchorEl(event.currentTarget);
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setAnchorEl(null), 120);
  };

  return (
    <>
      {/* No tooltip here: the menu pops open on hover, so a label tag would just
          overlap it (and toggling the tooltip on hover left it stuck open). */}
      <Box
        aria-label={link.label}
        sx={railButtonSx(Boolean(anchorEl))}
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
      >
        {link.icon}
      </Box>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        // Overshoot easing so the menu springs out of the rail instead of fading in.
        TransitionProps={{ timeout: 260, easing: "cubic-bezier(0.34, 1.56, 0.64, 1)" }}
        // Let hover pass through the backdrop; the paper re-enables pointer events.
        sx={{ pointerEvents: "none" }}
        disableAutoFocus
        disableEnforceFocus
        disableAutoFocusItem
        slotProps={{
          paper: {
            onMouseEnter: cancelClose,
            onMouseLeave: scheduleClose,
            sx: {
              pointerEvents: "auto",
              minWidth: 200,
              ml: 1,
              border: "1px solid",
              borderColor: (theme) => alpha(theme.palette.grey[500], 0.32),
              boxShadow: "0 12px 32px rgba(22, 28, 36, 0.32)"
            }
          }
        }}
      >
        {items.map((item, index) => (
          <MenuItem
            key={item.label}
            component="a"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setAnchorEl(null)}
            sx={{
              opacity: 0,
              animation: "railMenuItemIn 220ms ease-out forwards",
              animationDelay: `${60 + index * 45}ms`,
              "@keyframes railMenuItemIn": {
                from: { opacity: 0, transform: "translateX(-8px)" },
                to: { opacity: 1, transform: "none" }
              }
            }}
          >
            <ListItemIcon sx={{ color: "primary.dark", minWidth: "auto", mr: 0 }}>
              <Iconify icon={item.iconifyIcon} width={20} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: 14 }}>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function RailButton({ link }: { link: RailLink }) {
  if (link.menuItems) {
    return <RailMenuButton link={link} />;
  }

  const button = <Box sx={railButtonSx(link.current)}>{link.icon}</Box>;

  return (
    <Tooltip title={link.label} placement="right">
      {link.href ? (
        <Box
          component="a"
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: "block", textDecoration: "none" }}
        >
          {button}
        </Box>
      ) : (
        button
      )}
    </Tooltip>
  );
}

export function OrderDocumentsNavRail({
  orderNumber,
  orderInfo
}: {
  orderNumber?: string;
  orderInfo: DocumentsOrderInfo | null;
}) {
  const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;
  const encodedOrder = orderNumber ? encodeURIComponent(orderNumber) : "";
  const stateCode = orderInfo?.stateCode;
  const isCommercial = orderInfo?.propertyType === "Commercial";
  // Madex orders are Madison 1031 exchange orders (order number prefixed "MADEX").
  // They get a trimmed, exchange-focused rail; the apps ported from the old Apps
  // drawer apply to regular orders only.
  const isMadexOrder = Boolean(orderNumber && orderNumber.toLowerCase().startsWith("madex"));

  // The search page shows the same rail, minus everything that needs an open order.
  const orderScopedLinks: RailLink[] = orderNumber
    ? [
        {
          label: "Commitment",
          icon: <CommitmentIcon />,
          href: `${momentumUrl}Commitment?orderNumber=${encodedOrder}`,
          external: true
        }
      ]
    : [];

  const moneyMenuItems: RailMenuItem[] = [
    ...(orderNumber
      ? [
          {
            label: "Wired",
            iconifyIcon: "ic:baseline-attach-money",
            href: `https://wired.mcres.com/title?orderNumber=${encodedOrder}`,
            external: true
          }
        ]
      : []),
    {
      label: "VerifyQ",
      iconifyIcon: "material-symbols:verified-outline",
      href: "http://verifyq.mcres.com/",
      external: true
    },
    {
      label: "Funding Packages",
      iconifyIcon: "mdi:package-variant-closed",
      href: orderNumber ? `/funding-packages/${encodedOrder}` : "/funding-packages"
    },
    ...(orderNumber
      ? [
          {
            label: "Ledger",
            iconifyIcon: "mdi:book-open-outline",
            href: `/ledger/${encodedOrder}`
          }
        ]
      : []),
    // Operating Transfers doesn't apply to Madex (1031 exchange) orders.
    ...(orderNumber && !isMadexOrder
      ? [
          {
            label: "Operating Transfers",
            iconifyIcon: "mdi:bank-transfer",
            href: `https://operatingtransfers.mcres.com/request/new?order=${encodedOrder}`,
            external: true
          }
        ]
      : [])
  ];

  // Full Intel destination list — used on the search/home page, where there's no
  // order (and so no property state) to pick a single destination from.
  const intelMenuItems: RailMenuItem[] = [
    {
      label: "Title - NY",
      iconifyIcon: "mdi:city-variant-outline",
      href: "https://madisondocs.com/title/ny/",
      external: true
    },
    {
      label: "Title - National",
      iconifyIcon: "mdi:earth",
      href: "https://madisonintel.com",
      external: true
    },
    {
      label: "NJ Title Production",
      iconifyIcon: "mdi:factory",
      href: "http://njcb.madisondocs.com/",
      external: true
    },
    {
      label: "NY Title Production",
      iconifyIcon: "mdi:factory",
      href: "http://nycb.madisondocs.com/",
      external: true
    },
    {
      label: "1031 Exchange",
      iconifyIcon: "mdi:swap-horizontal-bold",
      href: "https://1031.madisondocs.com/",
      external: true
    }
  ];

  // Ported from the former Apps drawer. Calculators keeps its sub-items as a rail
  // dropdown menu (same pattern as Money/Intel); the rest are plain links. The whole
  // group is hidden for Madex (1031 exchange) orders.
  const calculatorMenuItems: RailMenuItem[] = [
    {
      label: "Transfer Tax",
      iconifyIcon: "mdi:cash-multiple",
      href: "https://www.madisontitle.com/Title.Site/Frontend/Calculators/TransferTax.aspx",
      external: true
    },
    {
      label: "Title Fees - NY",
      iconifyIcon: "mdi:file-percent-outline",
      href: "https://www.madisontitle.com/Title.Site/Frontend/Calculators/TitleCalculator.aspx?state=NY",
      external: true
    },
    {
      label: "Title Fees - NJ",
      iconifyIcon: "mdi:file-percent-outline",
      href: "https://www.madisontitle.com/Title.Site/Frontend/Calculators/TitleCalculator.aspx?state=NJ",
      external: true
    },
    {
      label: "Mortgage Rate Calculator",
      iconifyIcon: "mdi:percent-outline",
      href: "https://www.madisontitle.com/Title.Site/Frontend/Calculators/MortgageCalculator.aspx",
      external: true
    },
    {
      label: "§1031 Exchange Calculator",
      iconifyIcon: "mdi:swap-horizontal-bold",
      href: "https://www.madisontitle.com/title.site/frontend/Calculators/1031ExchangeFees.aspx",
      external: true
    }
  ];

  const appLinks: RailLink[] = isMadexOrder
    ? []
    : [
        {
          label: "Calculators",
          icon: <Iconify icon="mdi:calculator" />,
          menuItems: calculatorMenuItems
        },
        // Flip Approvals and Policies are order-scoped.
        ...(orderNumber
          ? [
              {
                label: "Flip Approvals",
                icon: <Iconify icon="mdi:swap-horizontal" />,
                href: `/task-managementv1/flips?order=${encodedOrder}`
              },
              {
                label: "Policies",
                icon: <Iconify icon="mdi:file-alert-outline" />,
                href: `/policy-issues?orderNumber=${encodedOrder}`
              }
            ]
          : []),
        {
          label: "Legal Forms",
          icon: <Iconify icon="mdi:file-document-multiple-outline" />,
          href: `https://www.madisontitle.com/Title.Site/Frontend/LegalForms/${
            stateCode ? `FormsLibrary.aspx?tabID=&state=${stateCode}` : "Forms.aspx"
          }`,
          external: true
        },
        {
          label: "Reports",
          icon: <Iconify icon="mdi:chart-bar" />,
          href: "https://www.madisoncres.com/reporting/reports.aspx",
          external: true
        },
        // New Orders is a home-page shortcut; it has no meaning once a file is open.
        ...(orderNumber
          ? []
          : [
              {
                label: "New Orders",
                icon: <Iconify icon="mdi:format-list-bulleted" />,
                href: "https://internal.mcres.com/orders",
                external: true
              }
            ]),
        {
          label: "Single Upload",
          icon: <Iconify icon="mdi:barcode" />,
          href: "http://home/homefeatures/BarCodes/PrintBarCode.aspx",
          external: true
        }
      ];

  const links: RailLink[] = [
    ...orderScopedLinks,
    // Money shows everywhere; its menu adapts to context (on the search page the
    // order-scoped items drop out, leaving Verify + Funding Packages). On Madex
    // orders the Money menu holds Wired/Verify/Funding Packages, with Ledger kept
    // out as its own icon.
    ...(orderNumber && isMadexOrder
      ? [
          {
            label: "Money",
            icon: <MoneyTransferIcon />,
            menuItems: moneyMenuItems.filter((item) => item.label !== "Ledger")
          },
          ...moneyMenuItems
            .filter((item) => item.label === "Ledger")
            .map((item) => ({
              label: item.label,
              icon: <Iconify icon={item.iconifyIcon} width={20} />,
              href: item.href,
              external: item.external
            }))
        ]
      : [
          {
            label: "Money",
            icon: <MoneyTransferIcon />,
            menuItems: moneyMenuItems
          }
        ]),
    // Intel: with an order we go straight to a single destination (the exchange site
    // for Madex, otherwise the property state's Intel search). On the search page
    // there's no order/state, so fall back to the full list of destinations as a menu.
    orderNumber
      ? {
          label: "Intel",
          icon: <IntelIcon />,
          href: isMadexOrder ? EXCHANGE_INTEL_URL : buildStateIntelUrl(stateCode),
          external: true
        }
      : {
          label: "Intel",
          icon: <IntelIcon />,
          menuItems: intelMenuItems
        },
    // Reader, Closing Checklist and Recordables don't apply to Madex orders.
    ...(orderNumber && !isMadexOrder
      ? [
          {
            label: "Open Reader Tool",
            icon: <ReadIcon />,
            href: `https://readers.madisontitle.com/orderService/${encodedOrder}/${isCommercial ? "1" : "0"}`,
            external: true
          },
          {
            label: "Closing Checklist",
            icon: <ChecklistIcon />,
            href: `/task-management/closingchecklist?order=${encodedOrder}`
          },
          {
            label: "Recordable Documents",
            icon: <RecordablesIcon />,
            href: `/recordables/?order=${encodedOrder}`
          }
        ]
      : []),
    ...appLinks,
    // Madex (1031 exchange) reports.
    ...(orderNumber && isMadexOrder
      ? [
          {
            label: "Disbursement Summary Report",
            icon: <DisbursementSummaryIcon />,
            href: `https://exchangedisbursementsummaryreport.azurewebsites.net/home/index?ordernumber=${encodedOrder}&reportType=DisbursementSummary`,
            external: true
          },
          {
            label: "Disbursement Authorization Report",
            icon: <DisbursementAuthorizationIcon />,
            href: `https://exchangedisbursementsummaryreport.azurewebsites.net/home/index?ordernumber=${encodedOrder}&reportType=DisbursementAuthorization`,
            external: true
          }
        ]
      : [])
  ];

  return (
    <Box
      sx={{
        display: { xs: "none", lg: "flex" },
        flexDirection: "column",
        position: "fixed",
        left: 0,
        top: HEADER_HEIGHT,
        bottom: 0,
        width: NAV_RAIL_WIDTH,
        backgroundColor: "#000e48",
        zIndex: (theme) => theme.zIndex.appBar - 1
      }}
    >
      <Stack spacing={1} alignItems="center" sx={{ py: 2, flexGrow: 1, overflowY: "auto" }}>
        {links.map((link) => (
          <RailButton key={link.label} link={link} />
        ))}
      </Stack>
    </Box>
  );
}
